import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Card } from '../components/ui';

// Sonde del Pulse: PT100 TEMP-01..06, temperature delle XY-MD04 sul bus da
// TEMP-07 in poi, umidita' HUM-01 e seguenti.
const PT100_CHANNEL_COUNT = 6;

function isProbeSensorId(sensorId = '') {
  return /^(TEMP|HUM)-\d+$/.test(String(sensorId || ''));
}

function probeSortKey(sensorId = '') {
  const id = String(sensorId || '');
  const number = Number(id.replace(/\D/g, '')) || 0;

  return (id.startsWith('HUM-') ? 1000 : 0) + number;
}

function probeUnit(sensorId = '') {
  return String(sensorId || '').startsWith('HUM-') ? '%RH' : '°C';
}

// Etichetta del canale salvata in Dispositivi smart, altrimenti CHn / MBn / UMn.
function probeTitle(sensorId = '', device = null) {
  const configured = Array.isArray(device?.channels)
    ? device.channels.find((channel) => channel?.sensor_id === sensorId)
    : null;

  const number = Number(String(sensorId).replace(/\D/g, '')) || 0;

  const fallback = String(sensorId).startsWith('HUM-')
    ? `UM${number}`
    : number > PT100_CHANNEL_COUNT
    ? `MB${number - PT100_CHANNEL_COUNT}`
    : `CH${number}`;

  const label = configured?.label || fallback;

  return label === sensorId ? sensorId : `${label} (${sensorId})`;
}

function extractSeriesBySensorId(telemetry, sensorId) {
  return telemetry
    .slice()
    .reverse()
    .map((item) => {
      const match = (item.measures || []).find(
        (m) => m.sensor_id === sensorId
      );

      const raw = match?.value;

      const value =
        typeof raw === 'number'
          ? raw
          : typeof raw === 'string'
          ? Number(raw)
          : NaN;

      const date = new Date(item.createdAt);

      return {
        label: date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        fullLabel: date.toLocaleString(),
        value,
      };
    })
    .filter((point) => Number.isFinite(point.value));
}

function MiniLineChart({ title, unit, data }) {
  if (!data.length) {
    return (
      <Card className="p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-700">{title}</p>
        <p className="mt-3 text-sm text-gray-500">Nessun dato disponibile</p>
      </Card>
    );
  }

  const latest = data[data.length - 1]?.value;
  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const formatValue = (value) =>
    typeof value === 'number' ? value.toFixed(2) : value;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg">
        <p className="text-xs text-gray-500">{payload[0]?.payload?.fullLabel || label}</p>
        <p className="mt-1 text-sm font-semibold text-gray-800">
          {formatValue(payload[0].value)} {unit}
        </p>
      </div>
    );
  };

  return (
    <Card className="p-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-700">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {formatValue(latest)} <span className="text-lg font-medium text-gray-500">{unit}</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs shrink-0">
          <div className="rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
            <p className="text-gray-500">Min</p>
            <p className="mt-1 font-semibold text-gray-800">{formatValue(min)}</p>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
            <p className="text-gray-500">Max</p>
            <p className="mt-1 font-semibold text-gray-800">{formatValue(max)}</p>
          </div>
        </div>
      </div>

      <div className="mt-5 h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 10, right: 12, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              minTickGap={24}
              label={{
                value: 'Tempo',
                position: 'insideBottom',
                offset: -4,
                style: { fill: '#6b7280', fontSize: 12 },
              }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              domain={['auto', 'auto']}
              tickFormatter={(value) => `${value}`}
              label={{
                value: `${unit === '%RH' ? 'Umidità' : 'Temperatura'} (${unit})`,
                angle: -90,
                position: 'insideLeft',
                style: { fill: '#6b7280', fontSize: 12, textAnchor: 'middle' },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#2563eb"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default function TelemetriaRealTime() {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [devicesError, setDevicesError] = useState('');

  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [fridgeFilter, setFridgeFilter] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [telemetryError, setTelemetryError] = useState('');
  
  /*
    Un grafico per ogni sonda che il device ha trasmesso. Prima erano fissi
    TEMP-01..04: TEMP-05/06, le sonde del bus e le umidita' non comparivano.
  */
  const sensorSeries = useMemo(() => {
    const ids = new Set();

    telemetry.forEach((item) => {
      (item.measures || []).forEach((measure) => {
        if (isProbeSensorId(measure?.sensor_id)) {
          ids.add(measure.sensor_id);
        }
      });
    });

    return Array.from(ids)
      .sort((a, b) => probeSortKey(a) - probeSortKey(b))
      .map((sensorId) => ({
        sensorId,
        title: probeTitle(sensorId, selectedDevice),
        unit: probeUnit(sensorId),
        data: extractSeriesBySensorId(telemetry, sensorId),
      }));
  }, [telemetry, selectedDevice]);




  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      setDevicesError('');

      const res = await authFetch('/api/iot/devices');
      if (!res || !res.ok) {
        throw new Error('Errore nel recupero dei dispositivi');
      }

      const data = await res.json();
      const parsed = Array.isArray(data) ? data : [];

      setDevices(parsed);

      setSelectedDevice((prev) => {
        if (!parsed.length) return null;

        if (!prev?.device_id) {
          return parsed[0];
        }

        const updatedSelected = parsed.find(
          (d) => d.device_id === prev.device_id
        );

        return updatedSelected || parsed[0];
      });
    } catch (error) {
      console.error('Errore fetch devices:', error);
      setDevicesError('Impossibile recuperare i dispositivi.');
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchTelemetry = async (deviceId, signal) => {
    try {
      setLoadingTelemetry(true);
      setTelemetryError('');

      const res = await authFetch(`/api/iot/devices/${deviceId}/telemetry`, {
        signal,
      });

      if (!res || !res.ok) {
        throw new Error('Errore nel recupero delle telemetrie');
      }

      const data = await res.json();

    if (!signal || !signal.aborted) {
      setTelemetry(Array.isArray(data) ? data : []);
    }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('Errore fetch telemetry:', error);
      setTelemetryError('Impossibile recuperare le telemetrie del dispositivo.');
    } finally {
    if (!signal || !signal.aborted) {
      setLoadingTelemetry(false);
    }
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);
  
  const canAutoRefreshTelemetry = selectedDevice?.pairing_status === 'paired';
  
  useEffect(() => {
    if (!selectedDevice?.device_id) return;
    if (!canAutoRefreshTelemetry) return;

    let intervalId;
    let controller = new AbortController();

    const loadTelemetry = async () => {
      controller.abort();
      controller = new AbortController();
      await fetchTelemetry(selectedDevice.device_id, controller.signal);
    };

    loadTelemetry();

    intervalId = setInterval(() => {
      loadTelemetry();
    }, 5000);

    return () => {
      clearInterval(intervalId);
      controller.abort();
    };
  }, [selectedDevice?.device_id, canAutoRefreshTelemetry]);


  useEffect(() => {
    if (!selectedDevice?.device_id) {
      setTelemetry([]);
      setTelemetryError('');
      return;
    }

    if (selectedDevice.pairing_status !== 'paired') {
      setTelemetry([]);
      setTelemetryError('');
    }
  }, [selectedDevice?.device_id, selectedDevice?.pairing_status]);


  const siteOptions = useMemo(
    () => [...new Set(devices.map((d) => d.installation_site).filter(Boolean))],
    [devices]
  );

  const roomOptions = useMemo(
    () => [...new Set(devices.map((d) => d.room).filter(Boolean))],
    [devices]
  );

  const fridgeOptions = useMemo(
    () => [...new Set(devices.map((d) => d.fridge_serial).filter(Boolean))],
    [devices]
  );

  const technicianOptions = useMemo(
    () => [...new Set(devices.map((d) => d.installed_by).filter(Boolean))],
    [devices]
  );

  const getPairingBadge = (status) => {
    switch (status) {
      case 'paired':
        return {
          label: 'Associato',
          className: 'bg-emerald-100 text-emerald-700',
        };
      case 'provisioning':
        return {
          label: 'In riconfigurazione',
          className: 'bg-amber-100 text-amber-700',
        };
      case 'unpaired':
        return {
          label: 'Disassociato',
          className: 'bg-slate-100 text-slate-700',
        };
      case 'error':
        return {
          label: 'Errore',
          className: 'bg-red-100 text-red-700',
        };
      case 'never_paired':
      default:
        return {
          label: 'Mai associato',
          className: 'bg-gray-100 text-gray-600',
        };
    }
  };

  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      const q = search.trim().toLowerCase();

      const matchesSearch =
        !q ||
        device.device_id?.toLowerCase().includes(q) ||
        device.machine_id?.toLowerCase().includes(q) ||
        device.site_id?.toLowerCase().includes(q) ||
        device.installation_site?.toLowerCase().includes(q) ||
        device.room?.toLowerCase().includes(q) ||
        device.fridge_serial?.toLowerCase().includes(q) ||
        device.customer_name?.toLowerCase().includes(q) ||
        device.installed_by?.toLowerCase().includes(q);

      const matchesSite = !siteFilter || device.installation_site === siteFilter;
      const matchesRoom = !roomFilter || device.room === roomFilter;
      const matchesFridge = !fridgeFilter || device.fridge_serial === fridgeFilter;
      const matchesTechnician =
        !technicianFilter || device.installed_by === technicianFilter;

      return (
        matchesSearch &&
        matchesSite &&
        matchesRoom &&
        matchesFridge &&
        matchesTechnician
      );
    });
  }, [devices, search, siteFilter, roomFilter, fridgeFilter, technicianFilter]);

  useEffect(() => {
    if (!selectedDevice && filteredDevices.length > 0) {
      setSelectedDevice(filteredDevices[0]);
      return;
    }

    if (
      selectedDevice &&
      !filteredDevices.some((d) => d.device_id === selectedDevice.device_id)
    ) {
      setSelectedDevice(filteredDevices[0] || null);
    }
  }, [filteredDevices, selectedDevice]);

  const latestTelemetry = telemetry[0];

  const visibleMeasures = useMemo(() => {
    if (!latestTelemetry?.measures) return [];

    return latestTelemetry.measures
      .filter((measure) => isProbeSensorId(measure.sensor_id))
      .sort((a, b) => probeSortKey(a.sensor_id) - probeSortKey(b.sensor_id));
  }, [latestTelemetry]);


  return (
    <div className="min-h-screen bg-gray-100 pb-8 sm:pb-10">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 pt-6 sm:pt-10 space-y-5 sm:space-y-6">
        <Card className="shadow-md p-4 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Telemetria real time
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Filtra i dispositivi e consulta i dati telemetrici. Il refresh automatico è attivo solo per i dispositivi associati.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full md:w-auto px-4 py-3 md:py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
            >
              Torna alla dashboard
            </button>
          </div>
        </Card>

        <Card className="shadow-md p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Filtri dispositivi
          </h2>

          <div className="grid gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-5">
            <input
              type="text"
              placeholder="Cerca per device, luogo, camera..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
            />

            <select
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">Tutti i luoghi</option>
              {siteOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <select
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">Tutte le camere</option>
              {roomOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <select
              value={fridgeFilter}
              onChange={(e) => setFridgeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">Tutte le matricole</option>
              {fridgeOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <select
              value={technicianFilter}
              onChange={(e) => setTechnicianFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">Tutti i tecnici</option>
              {technicianOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <div className="grid gap-5 sm:gap-6 xl:grid-cols-[0.95fr_1.45fr]">
          <Card className="shadow-md p-4 sm:p-6 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                Dispositivi filtrati
              </h2>

              <button
                type="button"
                onClick={fetchDevices}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition"
              >
                Aggiorna
              </button>
            </div>

            {loadingDevices && (
              <p className="text-sm text-gray-500">Caricamento dispositivi...</p>
            )}

            {!loadingDevices && devicesError && (
              <p className="text-sm text-red-600">{devicesError}</p>
            )}

            {!loadingDevices && !devicesError && filteredDevices.length === 0 && (
              <p className="text-sm text-gray-500">Nessun dispositivo trovato.</p>
            )}

            <div className="space-y-3">
              {filteredDevices.map((device) => {
                const active = selectedDevice?.device_id === device.device_id;

                return (
                  <button
                    key={device._id || device.device_id}
                    type="button"
                    onClick={() => setSelectedDevice(device)}
                    className={`w-full text-left rounded-xl border p-4 transition min-w-0 ${
                      active
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <p className="text-sm sm:text-base font-semibold text-gray-800 break-all">
                      {device.device_id}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 break-words">
                      {device.installation_site || '-'} · {device.room || '-'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 break-words">
                      Matricola: {device.fridge_serial || '-'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 break-words">
                      Tecnico: {device.installed_by || '-'}
                    </p>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card className="shadow-md p-4 sm:p-6 min-w-0">
            {!selectedDevice && (
              <>
                <h2 className="text-xl font-semibold text-gray-800">
                  Dettaglio telemetria
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  Seleziona un dispositivo dalla colonna a sinistra.
                </p>
              </>
            )}

            {selectedDevice && (
              <div className="space-y-5 sm:space-y-6 min-w-0">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-xl font-semibold text-gray-800 break-all">
                      {selectedDevice.device_id}
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 break-words">
                      {selectedDevice.installation_site || '-'} ·{' '}
                      {selectedDevice.room || '-'} ·{' '}
                      {selectedDevice.fridge_serial || '-'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 break-words">
                      Tecnico: {selectedDevice.installed_by || '-'}
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Stato pairing:{' '}
                      <strong>{getPairingBadge(selectedDevice.pairing_status).label}</strong>
                    </p>
                  </div>

                  <div
                    className={`inline-flex self-start rounded-full px-4 py-2 text-sm font-medium ${
                      canAutoRefreshTelemetry
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {canAutoRefreshTelemetry
                      ? 'refresh automatico: 5s'
                      : 'refresh automatico disattivato'}
                  </div>

                  {selectedDevice && !canAutoRefreshTelemetry && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      Il refresh automatico della telemetria è disponibile solo dopo il pairing del dispositivo.
                    </div>
                  )}

                </div>

                {loadingTelemetry && (
                  <p className="text-sm text-gray-500">Caricamento telemetrie...</p>
                )}

                {!loadingTelemetry && telemetryError && (
                  <p className="text-sm text-red-600">{telemetryError}</p>
                )}

                {latestTelemetry && (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        Ultimo aggiornamento
                      </p>
                      <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800 break-words">
                        {new Date(latestTelemetry.createdAt).toLocaleString()}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 min-w-0">
                      <p className="text-sm font-medium text-gray-700">IP / RSSI</p>
                      <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800 break-all">
                        {latestTelemetry.ip_address || '-'}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        RSSI: {latestTelemetry.wifi_rssi ?? '-'}
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        Stato connessione
                      </p>
                      <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800 break-words">
                        {String(latestTelemetry.status?.wifi_connected)}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 break-words">
                        uptime: {latestTelemetry.status?.uptime_ms ?? '-'} ms
                      </p>
                    </div>

                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        Memoria libera
                      </p>
                      <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800">
                        {latestTelemetry.status?.free_heap ?? '-'}
                      </p>
                    </div>
                  </div>
                )}

              <div className="grid gap-4 xl:grid-cols-2">
                {sensorSeries.map((series) => (
                  <MiniLineChart
                    key={series.sensorId}
                    title={series.title}
                    unit={series.unit}
                    data={series.data}
                  />
                ))}
              </div>

                {latestTelemetry && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                    <p className="text-sm font-medium text-gray-700">
                      Ultime misure ricevute
                    </p>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {visibleMeasures.map((measure, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-xl border border-gray-200 p-4 min-w-0"
                        >
                          <p className="text-sm font-medium text-gray-700 break-all">
                            {probeTitle(measure.sensor_id, selectedDevice)}
                          </p>
                          <p className="mt-1 text-sm text-gray-500 break-words">
                            {measure.sensor_type}
                          </p>
                          <p className="mt-2 text-xl sm:text-2xl font-semibold text-gray-800 break-words">
                            {measure.value} {measure.unit === 'C' ? '°C' : measure.unit}
                          </p>
                          <p className="mt-1 text-xs text-gray-500 break-words">
                            quality: {measure.quality_flag}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!loadingTelemetry && !telemetryError && telemetry.length === 0 && (
                  <p className="text-sm text-gray-500">
                    Nessuna telemetria trovata per il dispositivo selezionato.
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}